import { useEffect, useState, useCallback } from "react";
import { CATEGORIES, CONTENT_TYPES, LIFE_MOMENTS, RESOURCES, resourceBySlug, categoryBySlug, typeLabel } from "../data/hub";

const API = process.env.REACT_APP_BACKEND_URL;
const TKEY = "heard_admin_token";
const SENSITIVE = ["health-wellness", "food-nutrition", "work-money"];

const api = async (path, { method = "GET", body, token } = {}) => {
  const res = await fetch(`${API}/api${path}`, {
    method,
    headers: { "Content-Type": "application/json", "X-Admin-Token": token || localStorage.getItem(TKEY) || "" },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (res.status === 204) return null;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);
  return data;
};

const slugLabel = (s) => resourceBySlug(s)?.title || categoryBySlug(s)?.title || s;
const EVENT_LABELS = {
  page_view: "Page views", newsletter_signup: "Newsletter signups", early_access_signup: "Early-access signups",
  get_app_clicked: "Get the App clicks", join_community_clicked: "Join Community clicks",
  finds_clicked: "HEARD Finds clicks", resource_opened: "Resource opens",
  category_selected: "Category selections", search_used: "Searches", life_moment_selected: "Life moments",
};
const CONVERSION_KEYS = ["newsletter_signup", "early_access_signup", "get_app_clicked", "join_community_clicked", "finds_clicked"];
const pretty = (s) => (s === "/" ? "Home (/)" : s);
const TrendChip = ({ t }) => {
  if (!t || t.delta_pct === null || t.delta_pct === undefined) return null;
  const up = t.delta_pct >= 0;
  return (
    <span className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-[0.65rem] font-semibold ${up ? "bg-sage/15 text-sage-deep" : "bg-red-500/10 text-red-600"}`}>
      {up ? "▲" : "▼"} {Math.abs(t.delta_pct)}%
    </span>
  );
};
const StatList = ({ title, rows, fmt = (x) => x, testid }) => (
  <div className="rounded-2xl border border-line bg-white/70 p-5" data-testid={testid}>
    <h3 className="font-serif text-lg font-semibold text-heading">{title}</h3>
    <ul className="mt-3 space-y-1.5 text-sm">
      {rows.length ? rows.map(([k, n]) => (
        <li key={k} className="flex justify-between gap-3 text-ink/75">
          <span className="truncate">{fmt(k)}</span>
          <span className="font-semibold text-charcoal">{n}</span>
        </li>
      )) : <li className="text-ink/40">No data yet.</li>}
    </ul>
  </div>
);

const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="text-xs font-medium uppercase tracking-[0.12em] text-taupe">{label}</span>
    {children}
    {hint && <span className="mt-1 block text-[0.7rem] text-ink/50">{hint}</span>}
  </label>
);
const inputCls = "mt-1.5 w-full rounded-xl border border-line bg-white px-3.5 py-2.5 text-sm text-ink outline-none focus:border-sage";

// ---------------- Login ----------------
const Login = ({ onOk }) => {
  const [tok, setTok] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api("/admin/verify", { token: tok });
      localStorage.setItem(TKEY, tok);
      onOk();
    } catch (e2) { setErr("Incorrect access key."); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex min-h-screen items-center justify-center bg-cream px-6" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-sm rounded-3xl border border-line bg-white/80 p-8">
        <h1 className="serif-display text-2xl text-charcoal">HEARD · Founder</h1>
        <p className="mt-1 text-sm text-ink/60">Enter your access key to continue.</p>
        <input type="password" value={tok} onChange={(e) => setTok(e.target.value)} placeholder="Access key" className={inputCls} data-testid="admin-token-input" autoFocus />
        {err && <p className="mt-2 text-sm text-red-600" data-testid="admin-login-error">{err}</p>}
        <button disabled={busy} className="mt-4 w-full rounded-full bg-sage-deep px-5 py-3 text-xs font-medium uppercase tracking-[0.14em] text-cream disabled:opacity-60" data-testid="admin-login-submit">
          {busy ? "Checking…" : "Enter"}
        </button>
      </form>
    </div>
  );
};

// ---------------- Analytics ----------------
const Analytics = () => {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    setData(null); setErr("");
    api(`/admin/analytics/summary${days ? `?days=${days}` : ""}`).then(setData).catch((e) => setErr(e.message));
  }, [days]);
  const entries = (obj, n = 10) => Object.entries(obj || {}).slice(0, n);
  const utm = data?.utm || {};
  const hasUtm = entries(utm.source).length || entries(utm.medium).length || entries(utm.campaign).length;
  return (
    <div data-testid="admin-analytics">
      <div className="mb-6 flex gap-2">
        {[["Last 7 days", 7], ["Last 30 days", 30], ["All time", null]].map(([l, v]) => (
          <button key={l} onClick={() => setDays(v)} data-testid={`analytics-range-${v || "all"}`}
            className={`rounded-full border px-4 py-2 text-xs font-medium ${days === v ? "border-sage-deep bg-sage-deep text-cream" : "border-line bg-white text-sage-deep"}`}>{l}</button>
        ))}
      </div>
      {err && <p className="text-red-600" data-testid="analytics-error">{err}</p>}
      {data && (
        <>
          {/* Traffic headline */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div className="rounded-2xl border border-line bg-white/70 p-5" data-testid="traffic-visits">
              <p className="serif-display text-3xl text-charcoal">{data.visits ?? 0}<TrendChip t={data.trend?.visits} /></p>
              <p className="mt-1 text-xs text-ink/60">Visits (sessions)</p>
            </div>
            <div className="rounded-2xl border border-line bg-white/70 p-5" data-testid="traffic-pageviews">
              <p className="serif-display text-3xl text-charcoal">{data.page_views ?? 0}<TrendChip t={data.trend?.page_views} /></p>
              <p className="mt-1 text-xs text-ink/60">Page views</p>
            </div>
            <div className="rounded-2xl border border-line bg-white/70 p-5" data-testid="traffic-events">
              <p className="serif-display text-3xl text-charcoal">{data.total ?? 0}</p>
              <p className="mt-1 text-xs text-ink/60">Total events tracked</p>
            </div>
            <div className="rounded-2xl border border-line bg-white/70 p-5" data-testid="traffic-devices">
              <p className="text-sm text-ink/70">
                {entries(data.devices).length
                  ? entries(data.devices).map(([d, n]) => <span key={d} className="mr-3 capitalize"><b className="text-charcoal">{n}</b> {d}</span>)
                  : <span className="text-ink/40">No device data yet</span>}
              </p>
              <p className="mt-2 text-xs text-ink/60">Devices</p>
            </div>
          </div>

          {/* Traffic detail */}
          <h2 className="serif-display mt-8 mb-4 text-xl text-charcoal">Website traffic</h2>
          <div className="grid gap-5 lg:grid-cols-2">
            <StatList title="Top pages" testid="analytics-top-pages" rows={entries(data.top_pages, 12)} fmt={pretty} />
            <StatList title="Top entry / landing pages" testid="analytics-entry-pages" rows={entries(data.entry_pages)} fmt={pretty} />
            <StatList title="Referral sources" testid="analytics-referrers" rows={entries(data.referrers)} />
            <StatList title="Top search terms" testid="analytics-search-terms" rows={entries(data.top_labels?.search_used)} fmt={(s) => `"${s}"`} />
          </div>

          {hasUtm ? (
            <>
              <h2 className="serif-display mt-8 mb-4 text-xl text-charcoal">Campaigns (UTM)</h2>
              <div className="grid gap-5 sm:grid-cols-3">
                <StatList title="Source" testid="analytics-utm-source" rows={entries(utm.source)} />
                <StatList title="Medium" testid="analytics-utm-medium" rows={entries(utm.medium)} />
                <StatList title="Campaign" testid="analytics-utm-campaign" rows={entries(utm.campaign)} />
              </div>
            </>
          ) : (
            <p className="mt-6 text-xs text-ink/45" data-testid="analytics-utm-empty">No campaign (UTM) traffic in this period. UTM data appears automatically when visitors arrive via tagged links.</p>
          )}

          {/* Conversions */}
          <h2 className="serif-display mt-8 mb-4 text-xl text-charcoal">Conversions & engagement</h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {CONVERSION_KEYS.map((k) => (
              <div key={k} className="rounded-2xl border border-line bg-white/70 p-4" data-testid={`stat-${k}`}>
                <p className="serif-display text-3xl text-charcoal">{data.by_event?.[k] || 0}</p>
                <p className="mt-1 text-xs text-ink/60">{EVENT_LABELS[k]}</p>
              </div>
            ))}
          </div>

          {/* Content discovery */}
          <h2 className="serif-display mt-8 mb-4 text-xl text-charcoal">Content discovery</h2>
          <div className="grid gap-5 lg:grid-cols-2">
            <StatList title="Top resources opened" testid="analytics-top-resources" rows={entries(data.top_labels?.resource_opened)} fmt={slugLabel} />
            <StatList title="Top categories" testid="analytics-top-categories" rows={entries(data.top_labels?.category_selected)} fmt={slugLabel} />
            <StatList title="Top life moments" testid="analytics-top-lifemoments" rows={entries(data.top_labels?.life_moment_selected)} />
            <StatList title="Top HEARD Finds clicked" testid="analytics-top-finds" rows={entries(data.top_labels?.finds_clicked)} />
          </div>
        </>
      )}
    </div>
  );
};

// ---------------- Newsletter ----------------
const FIRST_EDITION = {
  subject: "A little HEARD in your inbox \uD83C\uDF3F",
  headline: "Welcome to A Little HEARD",
  featSlug: "understanding-everyday-stress",
  worthAsking: "What is one small thing I can do today for my well-being?",
  encouragement: "Be gentle with yourself. You're doing better than you think.",
  body: [
    "We're so glad you're here.",
    "Each week, we'll share gentle insights, supportive resources, and small reminders to help you feel more connected — to yourself and to others.",
  ].join("\n"),
};

const Newsletter = () => {
  const [hist, setHist] = useState(null);
  const [subject, setSubject] = useState("A little HEARD in your inbox 🌿");
  const [headline, setHeadline] = useState("");
  const [body, setBody] = useState("");
  const [worthAsking, setWorthAsking] = useState("");
  const [encouragement, setEncouragement] = useState("");
  const [featSlug, setFeatSlug] = useState("");
  const [testTo, setTestTo] = useState("");
  const [msg, setMsg] = useState("");
  const load = useCallback(() => api("/admin/newsletter/history").then(setHist).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const loadFirstEdition = () => {
    setSubject(FIRST_EDITION.subject);
    setHeadline(FIRST_EDITION.headline);
    setBody(FIRST_EDITION.body);
    setWorthAsking(FIRST_EDITION.worthAsking);
    setEncouragement(FIRST_EDITION.encouragement);
    setFeatSlug(FIRST_EDITION.featSlug);
    setMsg("First edition draft loaded — review, send a test, then send to all when you're ready.");
  };
  const featureObj = () => {
    const r = featSlug ? resourceBySlug(featSlug) : null;
    return r ? { title: r.title, dek: r.dek, url: `${window.location.origin}/read/${r.slug}`, image: r.image || "" } : null;
  };
  const compose = () => ({ subject, headline, body, feature: featureObj(), worth_asking: worthAsking, encouragement });
  const sendTest = async () => {
    setMsg("Sending test…");
    try { await api("/admin/newsletter/test", { method: "POST", body: { ...compose(), test_email: testTo || undefined } }); setMsg("Test sent."); }
    catch (e) { setMsg(e.message); }
  };
  const sendAll = async () => {
    if (!window.confirm(`Send to ${hist?.subscriber_count || 0} subscribers?`)) return;
    setMsg("Sending…");
    try { const r = await api("/admin/newsletter/send", { method: "POST", body: compose() }); setMsg(`Sent to ${r.sent}/${r.recipients}.`); load(); }
    catch (e) { setMsg(e.message); }
  };
  return (
    <div className="grid gap-8 lg:grid-cols-[1.3fr_1fr]" data-testid="admin-newsletter">
      <div>
        <div className="flex items-center justify-between gap-3">
          <h2 className="serif-display text-xl text-charcoal">Compose</h2>
          <button onClick={loadFirstEdition} className="rounded-full border border-sage-deep px-4 py-2 text-xs font-medium uppercase tracking-[0.12em] text-sage-deep hover:bg-sage-deep hover:text-cream" data-testid="nl-load-first-edition">Load first edition</button>
        </div>
        <div className="mt-4 space-y-4">
          <Field label="Subject"><input value={subject} onChange={(e) => setSubject(e.target.value)} className={inputCls} data-testid="nl-subject" /></Field>
          <Field label="Headline"><input value={headline} onChange={(e) => setHeadline(e.target.value)} className={inputCls} data-testid="nl-headline" /></Field>
          <Field label="Message" hint="One paragraph per line."><textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} className={inputCls} data-testid="nl-body" /></Field>
          <Field label="Worth asking this week (optional)"><input value={worthAsking} onChange={(e) => setWorthAsking(e.target.value)} placeholder="A gentle reflection question" className={inputCls} data-testid="nl-worth-asking" /></Field>
          <Field label="Encouragement (optional)"><input value={encouragement} onChange={(e) => setEncouragement(e.target.value)} placeholder="A small, warm note" className={inputCls} data-testid="nl-encouragement" /></Field>
          <Field label="Feature a resource (optional)">
            <select value={featSlug} onChange={(e) => setFeatSlug(e.target.value)} className={inputCls} data-testid="nl-feature">
              <option value="">None</option>
              {RESOURCES.map((r) => <option key={r.slug} value={r.slug}>{r.title}</option>)}
            </select>
          </Field>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1"><Field label="Test email (optional)"><input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@example.com" className={inputCls} data-testid="nl-test-email" /></Field></div>
            <button onClick={sendTest} className="rounded-full border border-sage-deep px-5 py-2.5 text-xs font-medium uppercase tracking-[0.12em] text-sage-deep" data-testid="nl-send-test">Send test</button>
            <button onClick={sendAll} className="rounded-full bg-sage-deep px-5 py-2.5 text-xs font-medium uppercase tracking-[0.12em] text-cream" data-testid="nl-send-all">Send to all</button>
          </div>
          {msg && <p className="text-sm text-sage-deep" data-testid="nl-msg">{msg}</p>}
        </div>
        <div className="mt-8">
          <h3 className="text-xs font-medium uppercase tracking-[0.12em] text-taupe">Preview</h3>
          <div className="mt-2 rounded-2xl border border-line bg-[#FBF8F1] p-6 text-center" data-testid="nl-preview">
            <p className="text-[0.6rem] uppercase tracking-[0.24em] text-[#B98A4E] font-semibold">A Little HEARD in Your Inbox</p>
            <h4 className="serif-display mt-3 text-2xl text-sage-deep">{headline || "Your headline"}</h4>
            <div className="mt-3">{body.split("\n").filter(Boolean).map((p, i) => <p key={i} className="mt-1.5 text-sm leading-relaxed text-ink/70">{p}</p>)}</div>
            {worthAsking && <div className="mt-4 rounded-xl border border-line bg-[#F5F0E7] p-3 text-left"><p className="text-[0.55rem] uppercase tracking-[0.16em] text-sage font-semibold">Worth asking this week</p><p className="mt-1 font-serif italic text-sage-deep">{worthAsking}</p></div>}
            {encouragement && <p className="mt-4 text-sm text-ink/70"><span className="text-[#B98A4E]">♡</span><br />{encouragement}</p>}
            {featureObj() && <div className="mt-4 rounded-xl border border-line bg-[#F5F0E7] p-3 text-left text-sm"><p className="text-[0.55rem] uppercase tracking-[0.16em] text-[#B98A4E] font-semibold">Featured read</p><b className="mt-1 block font-serif text-sage-deep">{featureObj().title}</b><p className="text-ink/60">{featureObj().dek}</p></div>}
            <p className="mt-4 font-serif italic text-sage-deep">You're not alone. We're in this together.</p>
          </div>
        </div>
      </div>
      <div>
        <div className="rounded-2xl border border-line bg-white/70 p-5">
          <p className="serif-display text-4xl text-charcoal" data-testid="nl-sub-count">{hist?.subscriber_count ?? "—"}</p>
          <p className="text-sm text-ink/60">newsletter subscribers</p>
        </div>
        <h3 className="mt-6 font-serif text-lg font-semibold text-heading">Recent signups</h3>
        <ul className="mt-2 space-y-1 text-sm text-ink/70" data-testid="nl-recent">
          {(hist?.recent_signups || []).slice(0, 10).map((s, i) => <li key={i} className="flex justify-between gap-2"><span className="truncate">{s.email}</span><span className="text-ink/40">{(s.created_at || "").slice(0, 10)}</span></li>)}
          {!hist?.recent_signups?.length && <li className="text-ink/40">No signups yet.</li>}
        </ul>
        <h3 className="mt-6 font-serif text-lg font-semibold text-heading">Send history</h3>
        <ul className="mt-2 space-y-1 text-sm text-ink/70" data-testid="nl-history">
          {(hist?.sends || []).map((s) => <li key={s.id} className="flex justify-between gap-2"><span className="truncate">{s.subject}</span><span className="text-ink/40">{s.sent}/{s.recipients}</span></li>)}
          {!hist?.sends?.length && <li className="text-ink/40">No sends yet.</li>}
        </ul>
      </div>
    </div>
  );
};

// ---------------- Resource editor ----------------
const emptyRes = { slug: "", title: "", category: "joy-growth", type: "explains", dek: "", readTime: "3 min", status: "draft", tags: [], life_moments: [], featured: false, image: "", imageAlt: "", seoTitle: "", metaDescription: "", whatKnow: "", stillLearning: "", everyday: [], worthAsking: [], sources: [], related: [] };
const lines = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);
const commas = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);
const srcToText = (arr) => (arr || []).map((s) => `${s.title || ""} | ${s.org || ""} | ${s.date || ""} | ${s.url || ""}`).join("\n");
const textToSrc = (t) => lines(t).map((l) => { const [title, org, date, url] = l.split("|").map((x) => (x || "").trim()); return { title, org, date, url }; }).filter((s) => s.title);

const ResourceEditor = ({ initial, onSaved, onCancel }) => {
  const [f, setF] = useState({ ...emptyRes, ...initial, tags: (initial?.tags || []).join(", "), related: (initial?.related || []).join(", "), life_moments: (initial?.life_moments || []).join(", "), everyday: (initial?.everyday || []).join("\n"), worthAsking: (initial?.worthAsking || []).join("\n"), sources: srcToText(initial?.sources) });
  const [msg, setMsg] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const save = async (status) => {
    const payload = { ...f, status, tags: commas(f.tags), related: commas(f.related), life_moments: commas(f.life_moments), everyday: lines(f.everyday), worthAsking: lines(f.worthAsking), sources: textToSrc(f.sources) };
    if (!payload.slug || !payload.title) { setMsg("Title and slug are required."); return; }
    setMsg("Saving…");
    try {
      if (initial?.id) await api(`/admin/resources/${initial.id}`, { method: "PUT", body: payload });
      else await api("/admin/resources", { method: "POST", body: payload });
      onSaved();
    } catch (e) { setMsg(e.message); }
  };
  const needsSrc = SENSITIVE.includes(f.category) || f.type === "learning";
  return (
    <div className="rounded-2xl border border-line bg-white/70 p-6" data-testid="resource-editor">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Title"><input value={f.title} onChange={set("title")} className={inputCls} data-testid="re-title" /></Field>
        <Field label="Slug" hint="URL: /read/your-slug"><input value={f.slug} onChange={set("slug")} className={inputCls} data-testid="re-slug" /></Field>
        <Field label="Category"><select value={f.category} onChange={set("category")} className={inputCls} data-testid="re-category">{CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.title}</option>)}</select></Field>
        <Field label="Content format"><select value={f.type} onChange={set("type")} className={inputCls} data-testid="re-type">{CONTENT_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}</select></Field>
        <Field label="Read time"><input value={f.readTime} onChange={set("readTime")} className={inputCls} /></Field>
        <Field label="Status"><select value={f.status} onChange={set("status")} className={inputCls} data-testid="re-status"><option value="draft">Draft</option><option value="Needs Review">Needs Review</option><option value="Approved">Approved</option><option value="published">Published</option></select></Field>
      </div>
      <div className="mt-4"><Field label="Summary / excerpt"><input value={f.dek} onChange={set("dek")} className={inputCls} data-testid="re-dek" /></Field></div>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <Field label="Image URL"><input value={f.image} onChange={set("image")} className={inputCls} /></Field>
        <Field label="Image alt text"><input value={f.imageAlt} onChange={set("imageAlt")} className={inputCls} /></Field>
        <Field label="Tags / keywords" hint="comma-separated"><input value={f.tags} onChange={set("tags")} className={inputCls} data-testid="re-tags" /></Field>
        <Field label="Life moment tags" hint="comma-separated"><input value={f.life_moments} onChange={set("life_moments")} className={inputCls} /></Field>
        <Field label="Related resource slugs" hint="comma-separated"><input value={f.related} onChange={set("related")} className={inputCls} /></Field>
        <Field label="SEO title"><input value={f.seoTitle} onChange={set("seoTitle")} className={inputCls} /></Field>
      </div>
      <div className="mt-4"><Field label="Meta description"><input value={f.metaDescription} onChange={set("metaDescription")} className={inputCls} /></Field></div>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <Field label={f.type === "learning" ? "What's established" : "What we know"}><textarea rows={3} value={f.whatKnow} onChange={set("whatKnow")} className={inputCls} /></Field>
        <Field label={f.type === "learning" ? "Still being studied" : "Still learning"}><textarea rows={3} value={f.stillLearning} onChange={set("stillLearning")} className={inputCls} /></Field>
        <Field label="Everyday points" hint="one per line"><textarea rows={4} value={f.everyday} onChange={set("everyday")} className={inputCls} /></Field>
        <Field label="Questions worth asking" hint="one per line"><textarea rows={4} value={f.worthAsking} onChange={set("worthAsking")} className={inputCls} /></Field>
      </div>
      <div className="mt-4"><Field label="Sources" hint={`Format per line: title | organization | date | url${needsSrc ? "  ·  REQUIRED to publish this category" : ""}`}><textarea rows={3} value={f.sources} onChange={set("sources")} className={inputCls} data-testid="re-sources" /></Field></div>
      <label className="mt-4 flex items-center gap-2 text-sm text-ink/70"><input type="checkbox" checked={f.featured} onChange={set("featured")} data-testid="re-featured" /> Featured</label>
      {needsSrc && <p className="mt-3 rounded-lg bg-cream-deep/60 px-3 py-2 text-xs text-ink/60">This is health/food/money or research content — an educational disclaimer will show automatically and a source is required to publish.</p>}
      {msg && <p className="mt-3 text-sm text-sage-deep" data-testid="re-msg">{msg}</p>}
      <div className="mt-5 flex gap-3">
        <button onClick={() => save("draft")} className="rounded-full border border-sage-deep px-5 py-2.5 text-xs font-medium uppercase tracking-[0.12em] text-sage-deep" data-testid="re-save-draft">Save draft</button>
        <button onClick={() => save("published")} className="rounded-full bg-sage-deep px-5 py-2.5 text-xs font-medium uppercase tracking-[0.12em] text-cream" data-testid="re-publish">Publish</button>
        <button onClick={onCancel} className="rounded-full px-5 py-2.5 text-xs uppercase tracking-[0.12em] text-ink/50">Cancel</button>
      </div>
    </div>
  );
};

const ResourcesTab = () => {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api("/admin/resources").then(setList).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const del = async (r) => { if (window.confirm(`Delete "${r.title}"?`)) { await api(`/admin/resources/${r.id}`, { method: "DELETE" }); load(); } };
  if (editing) return <ResourceEditor initial={editing.id ? editing : null} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />;
  return (
    <div data-testid="admin-resources">
      <IdeaBox onDraft={(d) => setEditing(d)} />
      <button onClick={() => setEditing({})} className="mb-5 rounded-full bg-sage-deep px-5 py-2.5 text-xs font-medium uppercase tracking-[0.12em] text-cream" data-testid="new-resource-btn">+ New resource</button>
      <div className="overflow-hidden rounded-2xl border border-line">
        <table className="w-full text-left text-sm">
          <thead className="bg-cream-deep/50 text-xs uppercase tracking-[0.1em] text-taupe"><tr><th className="p-3">Title</th><th className="p-3">Category</th><th className="p-3">Format</th><th className="p-3">Status</th><th className="p-3"></th></tr></thead>
          <tbody data-testid="resource-rows">
            {list.map((r) => (
              <tr key={r.id} className="border-t border-line bg-white/60">
                <td className="p-3 text-charcoal">{r.title}</td>
                <td className="p-3 text-ink/60">{categoryBySlug(r.category)?.title || r.category}</td>
                <td className="p-3 text-ink/60">{typeLabel(r.type)}</td>
                <td className="p-3"><span className={`rounded-full px-2.5 py-0.5 text-xs ${r.status === "published" ? "bg-sage/15 text-sage-deep" : "bg-taupe/15 text-taupe"}`}>{r.status}</span></td>
                <td className="p-3 text-right"><button onClick={() => setEditing(r)} className="mr-3 text-sage-deep">Edit</button><button onClick={() => del(r)} className="text-red-500">Delete</button></td>
              </tr>
            ))}
            {!list.length && <tr><td colSpan={5} className="p-4 text-ink/40">No custom resources yet. The {RESOURCES.length} built-in resources are always live.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------------- Finds tab ----------------
const emptyFind = { title: "", note: "", why: "", tag: "", group: "wellness", image: "", link: "", partner: "", affiliateDisclosure: "", sponsored: false, status: "draft" };
const FindsTab = () => {
  const [list, setList] = useState([]);
  const [f, setF] = useState(null);
  const load = useCallback(() => api("/admin/finds").then(setList).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const save = async (status) => { try { const body = { ...f, status }; if (f.id) await api(`/admin/finds/${f.id}`, { method: "PUT", body }); else await api("/admin/finds", { method: "POST", body }); setF(null); load(); } catch (e) { alert(e.message); } };
  const del = async (x) => { if (window.confirm("Delete this find?")) { await api(`/admin/finds/${x.id}`, { method: "DELETE" }); load(); } };
  if (f) return (
    <div className="rounded-2xl border border-line bg-white/70 p-6" data-testid="find-editor">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Item name"><input value={f.title} onChange={set("title")} className={inputCls} data-testid="fe-title" /></Field>
        <Field label="Tag"><input value={f.tag} onChange={set("tag")} className={inputCls} /></Field>
        <Field label="Group"><select value={f.group} onChange={set("group")} className={inputCls}><option value="books">Books & Reading</option><option value="comfort">Home & Everyday Comfort</option><option value="food-kitchen">Food & Kitchen</option><option value="wellness">Wellness & Self-Care</option><option value="earth">Earth-Friendly Living</option><option value="tools">Helpful Tools & Resources</option></select></Field>
        <Field label="Status"><select value={f.status} onChange={set("status")} className={inputCls}><option value="draft">Draft</option><option value="Needs Review">Needs Review</option><option value="Approved">Approved</option><option value="published">Published</option></select></Field>
      </div>
      <div className="mt-4 space-y-4">
        <Field label="Description"><input value={f.note} onChange={set("note")} className={inputCls} /></Field>
        <Field label="Why we picked it"><input value={f.why} onChange={set("why")} className={inputCls} /></Field>
        <Field label="Image URL"><input value={f.image} onChange={set("image")} className={inputCls} /></Field>
        <Field label="External link (optional)"><input value={f.link || ""} onChange={set("link")} className={inputCls} /></Field>
        <Field label="Partner name (optional — inactive)"><input value={f.partner || ""} onChange={set("partner")} className={inputCls} /></Field>
        <Field label="Affiliate disclosure (optional — inactive)"><input value={f.affiliateDisclosure || ""} onChange={set("affiliateDisclosure")} className={inputCls} /></Field>
        <label className="flex items-center gap-2 text-sm text-ink/70"><input type="checkbox" checked={f.sponsored} onChange={set("sponsored")} /> Sponsored / partner (disclosure only — no ranking or tracking)</label>
      </div>
      <div className="mt-5 flex gap-3">
        <button onClick={() => save("draft")} className="rounded-full border border-sage-deep px-5 py-2.5 text-xs uppercase tracking-[0.12em] text-sage-deep">Save draft</button>
        <button onClick={() => save("published")} className="rounded-full bg-sage-deep px-5 py-2.5 text-xs uppercase tracking-[0.12em] text-cream">Publish</button>
        <button onClick={() => setF(null)} className="rounded-full px-5 py-2.5 text-xs uppercase tracking-[0.12em] text-ink/50">Cancel</button>
      </div>
    </div>
  );
  return (
    <div data-testid="admin-finds">
      <QuickFind onDone={load} />
      <button onClick={() => setF({ ...emptyFind })} className="mb-5 rounded-full bg-sage-deep px-5 py-2.5 text-xs font-medium uppercase tracking-[0.12em] text-cream" data-testid="new-find-btn">+ New find</button>
      <div className="space-y-2" data-testid="find-rows">
        {list.map((x) => (
          <div key={x.id} className="flex items-center justify-between rounded-xl border border-line bg-white/60 px-4 py-3">
            <div><span className="text-charcoal">{x.title}</span> <span className="ml-2 text-xs text-ink/50">{x.group} · {x.status}</span></div>
            <div><button onClick={() => setF(x)} className="mr-3 text-sm text-sage-deep">Edit</button><button onClick={() => del(x)} className="text-sm text-red-500">Delete</button></div>
          </div>
        ))}
        {!list.length && <p className="text-ink/40">No custom finds yet. The built-in curated finds are always live.</p>}
      </div>
    </div>
  );
};

// ---------------- Community Stories tab ----------------
const emptyStory = { slug: "", title: "", subtitle: "", subject: "", excerpt: "", image: "", intro: "", body: "", support_name: "Support", support_url: "", support_note: "", status: "draft" };
const StoriesTab = () => {
  const [list, setList] = useState([]);
  const [f, setF] = useState(null);
  const load = useCallback(() => api("/admin/stories").then(setList).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (status) => {
    const body = { ...f, status };
    if (!body.slug || !body.title) { alert("Title and slug are required."); return; }
    try { if (f.id) await api(`/admin/stories/${f.id}`, { method: "PUT", body }); else await api("/admin/stories", { method: "POST", body }); setF(null); load(); }
    catch (e) { alert(e.message); }
  };
  const del = async (x) => { if (window.confirm(`Delete "${x.title}"?`)) { await api(`/admin/stories/${x.id}`, { method: "DELETE" }); load(); } };
  const setStatus = async (x, status) => { await api(`/admin/stories/${x.id}`, { method: "PUT", body: { ...x, status } }); load(); };
  if (f) return (
    <div className="rounded-2xl border border-line bg-white/70 p-6" data-testid="story-editor">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Title"><input value={f.title} onChange={set("title")} className={inputCls} data-testid="se-title" /></Field>
        <Field label="Slug" hint="URL: /community-stories/your-slug"><input value={f.slug} onChange={set("slug")} className={inputCls} data-testid="se-slug" /></Field>
        <Field label="Subtitle"><input value={f.subtitle} onChange={set("subtitle")} className={inputCls} /></Field>
        <Field label="Subject (e.g. Troy, 37)"><input value={f.subject} onChange={set("subject")} className={inputCls} /></Field>
        <Field label="Status"><select value={f.status} onChange={set("status")} className={inputCls} data-testid="se-status"><option value="draft">Draft</option><option value="Needs Review">Needs Review</option><option value="Approved">Approved</option><option value="published">Published</option><option value="Archived">Archived</option></select></Field>
        <Field label="Image URL"><input value={f.image} onChange={set("image")} className={inputCls} /></Field>
      </div>
      <div className="mt-4 space-y-4">
        <Field label="Card excerpt"><input value={f.excerpt} onChange={set("excerpt")} className={inputCls} /></Field>
        <Field label="Opening paragraph"><textarea rows={3} value={f.intro} onChange={set("intro")} className={inputCls} /></Field>
        <Field label="Story body" hint="one paragraph per line"><textarea rows={6} value={f.body} onChange={set("body")} className={inputCls} /></Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Support button label"><input value={f.support_name} onChange={set("support_name")} className={inputCls} /></Field>
          <Field label="Support link (e.g. GoFundMe URL)"><input value={f.support_url} onChange={set("support_url")} className={inputCls} data-testid="se-support-url" /></Field>
        </div>
        <Field label="Support / permission note"><textarea rows={2} value={f.support_note} onChange={set("support_note")} className={inputCls} /></Field>
      </div>
      <div className="mt-5 flex gap-3">
        <button onClick={() => save("draft")} className="rounded-full border border-sage-deep px-5 py-2.5 text-xs uppercase tracking-[0.12em] text-sage-deep" data-testid="se-save-draft">Save draft</button>
        <button onClick={() => save("published")} className="rounded-full bg-sage-deep px-5 py-2.5 text-xs uppercase tracking-[0.12em] text-cream" data-testid="se-publish">Publish</button>
        <button onClick={() => setF(null)} className="rounded-full px-5 py-2.5 text-xs uppercase tracking-[0.12em] text-ink/50">Cancel</button>
      </div>
    </div>
  );
  return (
    <div data-testid="admin-stories">
      <button onClick={() => setF({ ...emptyStory })} className="mb-5 rounded-full bg-sage-deep px-5 py-2.5 text-xs font-medium uppercase tracking-[0.12em] text-cream" data-testid="new-story-btn">+ New story</button>
      <div className="space-y-2" data-testid="story-rows">
        {list.map((x) => (
          <div key={x.id} className="flex items-center justify-between gap-3 rounded-xl border border-line bg-white/60 px-4 py-3" data-testid={`story-row-${x.slug}`}>
            <div>
              <span className="text-charcoal">{x.title}</span> <span className="ml-2 text-xs text-ink/50">/{x.slug} · {x.status}</span>
              {x.submitter_email && <div className="mt-0.5 text-[0.7rem] text-ink/45">Submitted by {x.submitter_name} ({x.submitter_email}) · show as: {x.display_pref} · consent: {x.consent ? "yes" : "no"}</div>}
            </div>
            <div className="flex shrink-0 items-center gap-3">
              {x.status !== "published"
                ? <button onClick={() => setStatus(x, "published")} className="text-sm font-medium text-sage-deep" data-testid={`story-publish-${x.slug}`}>Publish</button>
                : <button onClick={() => setStatus(x, "Archived")} className="text-sm font-medium text-[#B98A4E]" data-testid={`story-unpublish-${x.slug}`}>Unpublish</button>}
              <button onClick={() => setF(x)} className="text-sm text-sage-deep">Edit</button>
              <button onClick={() => del(x)} className="text-sm text-red-500">Delete</button>
            </div>
          </div>
        ))}
        {!list.length && <p className="text-ink/40">No custom stories yet. Troy's built-in story is always live.</p>}
      </div>
    </div>
  );
};

// ---------------- Content growth helpers ----------------
const FIND_GROUP_OPTS = [["books","Books & Reading"],["comfort","Home & Everyday Comfort"],["food-kitchen","Food & Kitchen"],["wellness","Wellness & Self-Care"],["earth","Earth-Friendly Living"],["tools","Helpful Tools & Resources"]];

const IdeaBox = ({ onDraft }) => {
  const [topic, setTopic] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const gen = async () => {
    if (!topic.trim()) return;
    setBusy(true); setMsg("Drafting with AI — this takes a few seconds…");
    try { const d = await api("/admin/resources/from-idea", { method: "POST", body: { topic } }); setMsg(""); setTopic(""); onDraft(d); }
    catch (e) { setMsg(e.message || "Could not generate a draft."); }
    finally { setBusy(false); }
  };
  return (
    <div className="mb-5 rounded-2xl border border-line bg-sage/[0.06] p-5" data-testid="idea-box">
      <p className="text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-sage-deep">Create from idea</p>
      <p className="mt-1 text-sm text-ink/60">Enter a topic — HEARD drafts a resource for you to review. Nothing publishes automatically.</p>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. gentle morning routines for low-energy days" className={inputCls} data-testid="idea-input" />
        <button onClick={gen} disabled={busy} className="shrink-0 rounded-full bg-sage-deep px-6 py-2.5 text-xs font-medium uppercase tracking-[0.12em] text-cream disabled:opacity-50" data-testid="idea-generate-btn">{busy ? "Drafting…" : "Generate draft"}</button>
      </div>
      {msg && <p className="mt-2 text-sm text-sage-deep" data-testid="idea-msg">{msg}</p>}
    </div>
  );
};

const QuickFind = ({ onDone }) => {
  const [f, setF] = useState({ title: "", link: "", group: "wellness", note: "" });
  const [msg, setMsg] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const add = async () => {
    if (!f.title.trim()) { setMsg("Name is required."); return; }
    try { await api("/admin/finds", { method: "POST", body: { ...f, status: "draft" } }); setF({ title: "", link: "", group: "wellness", note: "" }); setMsg("Draft find added below."); onDone(); }
    catch (e) { setMsg(e.message); }
  };
  return (
    <div className="mb-5 rounded-2xl border border-line bg-sage/[0.06] p-5" data-testid="quick-find">
      <p className="text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-sage-deep">Quick add a find</p>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        <input value={f.title} onChange={set("title")} placeholder="Name" className={inputCls} data-testid="qf-name" />
        <input value={f.link} onChange={set("link")} placeholder="URL (optional)" className={inputCls} data-testid="qf-url" />
        <select value={f.group} onChange={set("group")} className={inputCls} data-testid="qf-category">{FIND_GROUP_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        <input value={f.note} onChange={set("note")} placeholder="Short note" className={inputCls} data-testid="qf-note" />
      </div>
      <div className="mt-3 flex items-center gap-3">
        <button onClick={add} className="rounded-full bg-sage-deep px-6 py-2.5 text-xs font-medium uppercase tracking-[0.12em] text-cream" data-testid="qf-add-btn">Add draft find</button>
        {msg && <span className="text-sm text-sage-deep" data-testid="qf-msg">{msg}</span>}
      </div>
    </div>
  );
};

const STATUS_STYLE = (s) => s === "published" ? "bg-sage/15 text-sage-deep" : s === "Needs Review" ? "bg-[#B98A4E]/15 text-[#B98A4E]" : s === "Approved" ? "bg-sage/25 text-sage-deep" : "bg-taupe/15 text-taupe";

const ContentQueue = () => {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const load = useCallback(() => api("/admin/content-queue").then(setRows).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const shown = rows.filter((r) => filter === "all" || r.status === filter);
  return (
    <div data-testid="content-queue">
      <div className="mb-5 flex flex-wrap gap-2">
        {["all", "draft", "Needs Review", "Approved", "published"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`queue-filter-${s.replace(/\s/g, "-")}`}
            className={`rounded-full border px-4 py-1.5 text-xs font-medium ${filter === s ? "border-sage-deep bg-sage-deep text-cream" : "border-line bg-white text-sage-deep"}`}>{s === "all" ? "All" : s}</button>
        ))}
      </div>
      <div className="overflow-hidden rounded-2xl border border-line">
        <table className="w-full text-left text-sm">
          <thead className="bg-cream-deep/50 text-xs uppercase tracking-[0.1em] text-taupe"><tr><th className="p-3">Type</th><th className="p-3">Title</th><th className="p-3">Category</th><th className="p-3">Status</th><th className="p-3">Last updated</th></tr></thead>
          <tbody data-testid="queue-rows">
            {shown.map((r) => (
              <tr key={`${r.ctype}-${r.id}`} className="border-t border-line bg-white/60">
                <td className="p-3 text-ink/60">{r.ctype}</td>
                <td className="p-3 text-charcoal">{r.title}</td>
                <td className="p-3 text-ink/60">{categoryBySlug(r.category)?.title || r.category}</td>
                <td className="p-3"><span className={`rounded-full px-2.5 py-0.5 text-xs ${STATUS_STYLE(r.status)}`}>{r.status}</span></td>
                <td className="p-3 text-ink/50">{(r.updated || "").slice(0, 10)}</td>
              </tr>
            ))}
            {!shown.length && <tr><td colSpan={5} className="p-4 text-ink/40">Nothing here yet. Drafts you create will appear in the queue.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------------- Shell ----------------
export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("queue");
  useEffect(() => {
    const t = localStorage.getItem(TKEY);
    if (t) api("/admin/verify", { token: t }).then(() => setAuthed(true)).catch(() => localStorage.removeItem(TKEY));
    document.title = "HEARD · Founder";
  }, []);
  if (!authed) return <Login onOk={() => setAuthed(true)} />;
  const tabs = [["queue", "Content Queue"], ["analytics", "Analytics"], ["newsletter", "Newsletter"], ["resources", "Resources"], ["finds", "HEARD Finds"], ["stories", "Community Stories"]];
  return (
    <div className="min-h-screen bg-cream" data-testid="admin-shell">
      <header className="border-b border-line bg-white/70">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div><span className="serif-display text-xl text-charcoal">HEARD</span> <span className="text-sm text-ink/50">· Founder tools</span></div>
          <button onClick={() => { localStorage.removeItem(TKEY); setAuthed(false); }} className="text-sm text-sage-deep" data-testid="admin-logout">Sign out</button>
        </div>
        <div className="mx-auto flex max-w-6xl gap-1 px-4">
          {tabs.map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
              className={`border-b-2 px-4 py-3 text-sm font-medium ${tab === k ? "border-sage-deep text-charcoal" : "border-transparent text-ink/50 hover:text-ink"}`}>{l}</button>
          ))}
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-6 py-10">
        {tab === "queue" && <ContentQueue />}
        {tab === "analytics" && <Analytics />}
        {tab === "newsletter" && <Newsletter />}
        {tab === "resources" && <ResourcesTab />}
        {tab === "finds" && <FindsTab />}
        {tab === "stories" && <StoriesTab />}
      </main>
    </div>
  );
}
